import {
  collection, doc, addDoc, updateDoc, getDocs,
  onSnapshot, query, where, orderBy, serverTimestamp,
  runTransaction, limit as firestoreLimit,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getEffectiveTenantId, tenantCollection, tenantDoc } from './tenantContext';

export const TICKET_CATEGORIES = [
  { value: 'patient_login', label: 'Patient Login Issue' },
  { value: 'careAppUser_login', label: 'Care App User Login Issue' },
  { value: 'device_pairing', label: 'Device Pairing' },
  { value: 'billing', label: 'Billing' },
  { value: 'app_bug', label: 'App Bug / Error' },
  { value: 'data_correction', label: 'Data Correction' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'training', label: 'Training / How-To' },
  { value: 'rpm_reading_issue', label: 'RPM Reading Issue' },
  { value: 'other', label: 'Other' },
];

export const TICKET_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export const TICKET_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

const OPEN_STATUSES = ['open', 'in_progress', 'waiting'];

export function subscribeToTickets(filters, userContext, onData) {
  let cancelled = false;
  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || cancelled) return;

    const col = tenantCollection(tenantId, 'helpdeskTickets');
    const constraints = [orderBy('createdAt', 'desc')];

    if (filters?.status) constraints.push(where('status', '==', filters.status));
    if (filters?.category) constraints.push(where('category', '==', filters.category));
    if (filters?.priority) constraints.push(where('priority', '==', filters.priority));

    // Care managers and supervisors are scoped client-side so a ticket is visible when it was
    // either submitted by, or assigned to, the user (or, for supervisors, their team). Firestore
    // can't OR across submittedBy.uid and assignedTo.uid in one query, and the read rule already
    // restricts access to the tenant, so filtering in memory is safe.
    const role = userContext?.role;
    const q = query(col, ...constraints);
    const unsub = onSnapshot(q, (snap) => {
      let tickets = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (role === 'care manager') {
        const uid = userContext.uid;
        tickets = tickets.filter((t) => t.submittedBy?.uid === uid || t.assignedTo?.uid === uid);
      } else if (role === 'supervisor') {
        const allowed = new Set([userContext.uid, ...(userContext?.teamUids || [])]);
        tickets = tickets.filter((t) => allowed.has(t.submittedBy?.uid) || allowed.has(t.assignedTo?.uid));
      }

      onData(tickets);
    }, (err) => {
      console.warn('Helpdesk tickets subscription error', err);
      onData([]);
    });

    if (cancelled) unsub();
    else onData._unsub = unsub;
  })();

  return () => {
    cancelled = true;
    if (onData._unsub) onData._unsub();
  };
}

export async function createTicket(ticketData) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant.');

  const counterRef = doc(db, 'tenants', tenantId, 'helpdeskMeta', 'ticketCounter');
  let ticketNumber;

  await runTransaction(db, async (txn) => {
    const counterSnap = await txn.get(counterRef);
    const last = counterSnap.exists() ? (counterSnap.data().lastNumber || 0) : 0;
    ticketNumber = last + 1;
    txn.set(counterRef, { lastNumber: ticketNumber }, { merge: true });
  });

  const col = tenantCollection(tenantId, 'helpdeskTickets');
  const docRef = await addDoc(col, {
    ticketNumber,
    subject: ticketData.subject,
    description: ticketData.description,
    category: ticketData.category,
    priority: ticketData.priority || 'medium',
    status: 'open',
    relatedPatientId: ticketData.relatedPatientId || null,
    relatedPatientName: ticketData.relatedPatientName || null,
    submittedBy: ticketData.submittedBy,
    assignedTo: null,
    attachments: ticketData.attachments || [],
    autoGenerated: ticketData.autoGenerated || false,
    autoGeneratedAction: ticketData.autoGeneratedAction || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    resolvedAt: null,
  });

  return { id: docRef.id, ticketNumber };
}

export async function updateTicketStatus(ticketId, newStatus) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  const patch = { status: newStatus, updatedAt: serverTimestamp() };
  if (newStatus === 'resolved') patch.resolvedAt = serverTimestamp();
  await updateDoc(tenantDoc(tenantId, 'helpdeskTickets', ticketId), patch);
}

export async function assignTicket(ticketId, assignee) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  await updateDoc(tenantDoc(tenantId, 'helpdeskTickets', ticketId), {
    assignedTo: assignee,
    updatedAt: serverTimestamp(),
  });
}

export function subscribeToTicketUpdates(ticketId, onData) {
  let cancelled = false;
  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || cancelled) return;
    const col = collection(db, 'tenants', tenantId, 'helpdeskTickets', ticketId, 'updates');
    const q = query(col, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.warn('Helpdesk ticket updates subscription error', ticketId, err);
      onData([]);
    });
    if (cancelled) unsub();
    else onData._unsub = unsub;
  })();
  return () => {
    cancelled = true;
    if (onData._unsub) onData._unsub();
  };
}

export async function addTicketUpdate(ticketId, { message, author, statusChange, attachments }) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  const col = collection(db, 'tenants', tenantId, 'helpdeskTickets', ticketId, 'updates');
  await addDoc(col, {
    message: message || '',
    author,
    statusChange: statusChange || null,
    attachments: attachments || [],
    createdAt: serverTimestamp(),
  });
  const patch = { updatedAt: serverTimestamp() };
  if (statusChange) {
    patch.status = statusChange;
    if (statusChange === 'resolved') patch.resolvedAt = serverTimestamp();
  }
  await updateDoc(tenantDoc(tenantId, 'helpdeskTickets', ticketId), patch);
}

export async function updateTicketAttachments(ticketId, attachments) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant.');
  await updateDoc(tenantDoc(tenantId, 'helpdeskTickets', ticketId), {
    attachments,
    updatedAt: serverTimestamp(),
  });
}

export async function uploadTicketAttachment(ticketId, file) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) throw new Error('Cannot determine tenant.');
  const ts = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `tenants/${tenantId}/helpdesk/${ticketId}/${ts}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { name: file.name, url, size: file.size, type: file.type };
}

export function subscribeToOpenTicketCount(userContext, onCount) {
  let cancelled = false;
  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || cancelled) return;
    const col = tenantCollection(tenantId, 'helpdeskTickets');
    const q = query(col, where('status', 'in', OPEN_STATUSES));
    const unsub = onSnapshot(q, (snap) => {
      let docs = snap.docs;
      const role = userContext?.role;
      if (role === 'care manager') {
        docs = docs.filter((d) => d.data().submittedBy?.uid === userContext.uid);
      } else if (role === 'supervisor' && userContext?.teamUids?.length > 0) {
        const allowed = new Set([userContext.uid, ...userContext.teamUids]);
        docs = docs.filter((d) => allowed.has(d.data().submittedBy?.uid));
      }
      onCount(docs.length);
    }, (err) => {
      console.warn('Helpdesk open ticket count subscription error', err);
      onCount(0);
    });
    if (cancelled) unsub();
    else onCount._unsub = unsub;
  })();
  return () => {
    cancelled = true;
    if (onCount._unsub) onCount._unsub();
  };
}

export async function createAutoTicket({ subject, description, category, action, actorUid, actorDisplayName, actorEmail, relatedPatientId, relatedPatientName }) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return;
  const counterRef = doc(db, 'tenants', tenantId, 'helpdeskMeta', 'ticketCounter');
  let ticketNumber;
  await runTransaction(db, async (txn) => {
    const counterSnap = await txn.get(counterRef);
    const last = counterSnap.exists() ? (counterSnap.data().lastNumber || 0) : 0;
    ticketNumber = last + 1;
    txn.set(counterRef, { lastNumber: ticketNumber }, { merge: true });
  });
  const col = tenantCollection(tenantId, 'helpdeskTickets');
  await addDoc(col, {
    ticketNumber,
    subject,
    description,
    category: category || 'other',
    priority: 'low',
    status: 'resolved',
    relatedPatientId: relatedPatientId || null,
    relatedPatientName: relatedPatientName || null,
    submittedBy: { uid: actorUid, displayName: actorDisplayName, email: actorEmail, role: 'system' },
    assignedTo: null,
    attachments: [],
    autoGenerated: true,
    autoGeneratedAction: action,
    notifyOnCreate: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    resolvedAt: serverTimestamp(),
  });
}

export async function getTeamUidsForSupervisor(supervisorUid) {
  try {
    const q = query(collection(db, 'users'), where('supervisorId', '==', supervisorUid));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.id);
  } catch {
    return [];
  }
}

export async function searchPatients(searchTerm) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !searchTerm?.trim()) return [];
  const col = tenantCollection(tenantId, 'patients');
  const snap = await getDocs(query(col, firestoreLimit(200)));
  const term = searchTerm.trim().toLowerCase();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const phone = (p.phone || '').toLowerCase();
      const mrn = (p.mrn || '').toLowerCase();
      return name.includes(term) || phone.includes(term) || mrn.includes(term);
    })
    .slice(0, 20);
}
